import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldAlert, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const RISK_COLORS = {
  low: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700", prohibited: "bg-red-100 text-red-700",
};

export default function OnboardingTab({ focusUserId = null }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ business_status: "", risk: "" });
  const [openCase, setOpenCase] = useState(null);
  const [focusHandled, setFocusHandled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.business_status) params.business_status = filters.business_status;
      if (filters.risk) params.risk = filters.risk;
      const { data } = await api.get("/admin/onboarding/queue", { params });
      setQueue(data.queue || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from the new-signup bell alert (/admin?tab=onboarding&user=<id>): auto-open that user's case.
  // RBAC/tenant-safe — we only match rows the staff queue already returned; if the user has no accessible
  // case (e.g., no business profile yet), we gracefully stay on the onboarding list.
  useEffect(() => {
    if (!focusUserId || focusHandled || loading) return;
    const row = queue.find((b) => b.owner_user_id === focusUserId);
    if (row) setOpenCase(row.business_id);
    else toast.info("No onboarding record for that account yet — showing the onboarding queue.");
    setFocusHandled(true);
  }, [focusUserId, focusHandled, loading, queue]);

  const runReverify = async () => {
    try {
      const { data } = await api.post("/admin/onboarding/reverification/run");
      const c = data.counts || {};
      const msg = c.skipped ? "Reverification already ran today"
        : `Reverification complete — ${c.expired_documents || 0} expired docs, ${c.reverification_flagged || 0} flagged, ${c.sanctions_rescreen_due || 0} rescreen due`;
      toast.success(msg);
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-onboarding-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="onb-filter-status" value={filters.business_status} onChange={(e) => setFilters({ ...filters, business_status: e.target.value })}
          className="h-9 px-3 rounded-md border border-slate-300 text-sm">
          <option value="">All statuses</option>
          {["draft", "duplicate_review_required", "manual_review", "compliance_blocked", "verified", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="onb-filter-risk" value={filters.risk} onChange={(e) => setFilters({ ...filters, risk: e.target.value })}
          className="h-9 px-3 rounded-md border border-slate-300 text-sm">
          <option value="">All risk</option>
          {["low", "medium", "high", "prohibited"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid="onb-refresh" onClick={load} className="h-9 px-3 rounded-md border border-slate-300 text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        <button data-testid="onb-run-reverify" onClick={runReverify} className="h-9 px-3 rounded-md bg-slate-800 text-white text-sm flex items-center gap-1.5"><ShieldAlert size={14} /> Run reverification job</button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-2.5">Business</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">Risk</th><th className="text-left px-4 py-2.5">Stage</th><th className="px-4 py-2.5"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {queue.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No businesses in queue.</td></tr>}
              {queue.map((b) => (
                <tr key={b.business_id} data-testid={`onb-row-${b.business_id}`}>
                  <td className="px-4 py-3 font-medium text-[#0A0F1C]">{b.legal_business_name}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{b.status}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${RISK_COLORS[b.risk_score] || "bg-slate-100"}`}>{b.risk_score}</span></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{b.onboarding_stage}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`onb-open-${b.business_id}`} onClick={() => setOpenCase(b.business_id)} className="text-[#2563EB] text-sm font-medium">Open case</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openCase && <CaseDrawer businessId={openCase} onClose={() => setOpenCase(null)} onChange={load} />}
    </div>
  );
}

function CaseDrawer({ businessId, onClose, onChange }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/onboarding/case/${businessId}`); setC(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [businessId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); await load(); onChange(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="onb-case-drawer">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400"><X size={20} /></button>
        {loading || !c ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div> : (
          <div className="space-y-5">
            <div>
              <h3 className="font-heading text-lg font-bold text-[#0A0F1C]">{c.legal_business_name}</h3>
              <div className="mt-1 flex gap-2 text-xs">
                <span className="px-2 py-0.5 rounded bg-slate-100">{c.status}</span>
                <span className={`px-2 py-0.5 rounded ${RISK_COLORS[c.risk_score] || "bg-slate-100"}`}>risk: {c.risk_score}</span>
              </div>
            </div>

            {c.duplicate_candidates?.length > 0 && (
              <Section title="Duplicate review">
                <p className="text-xs text-amber-700 mb-2">{c.duplicate_candidates.length} possible duplicate(s).</p>
                <div className="flex gap-2">
                  <button data-testid="onb-approve-new" onClick={() => act(() => api.post("/admin/onboarding/duplicate/resolve", { business_id: businessId, action: "approve_new", reason: "not a duplicate" }), "Approved as new")} className="h-8 px-3 rounded bg-emerald-600 text-white text-xs">Approve new</button>
                  <button data-testid="onb-reject-claim" onClick={() => act(() => api.post("/admin/onboarding/duplicate/resolve", { business_id: businessId, action: "reject_claim", reason: "duplicate" }), "Rejected")} className="h-8 px-3 rounded bg-red-600 text-white text-xs">Reject claim</button>
                </div>
              </Section>
            )}

            <Section title="Verification components">
              <div className="grid grid-cols-2 gap-1 text-xs">
                {Object.entries(c.components || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border border-slate-100 rounded px-2 py-1">
                    <span className="text-slate-500">{k}</span>
                    <button data-testid={`onb-approve-comp-${k}`} onClick={() => act(() => api.post("/admin/onboarding/component/decide", { business_id: businessId, component: k, status: "verified" }), `${k} verified`)}
                      className={`px-1.5 rounded ${v === "verified" ? "text-emerald-600 font-semibold" : "text-slate-400 hover:text-[#2563EB]"}`}>{v}</button>
                  </div>
                ))}
              </div>
            </Section>

            {c.representatives?.length > 0 && (
              <Section title="Representatives">
                {c.representatives.map((r) => (
                  <div key={r.user_id} className="flex items-center justify-between text-sm border border-slate-100 rounded px-2 py-1.5 mb-1">
                    <span>{r.legal_name} — {r.title} <span className="text-xs text-slate-400">({r.status})</span></span>
                    <div className="flex gap-1">
                      <button data-testid="onb-rep-verify" onClick={() => act(() => api.post("/admin/onboarding/representative/decide", { business_id: businessId, representative_user_id: r.user_id, decision: "verify" }), "Rep verified")} className="text-xs text-emerald-600">Verify</button>
                      <button data-testid="onb-rep-reject" onClick={() => act(() => api.post("/admin/onboarding/representative/decide", { business_id: businessId, representative_user_id: r.user_id, decision: "reject" }), "Rep rejected")} className="text-xs text-red-500">Reject</button>
                    </div>
                  </div>
                ))}
              </Section>
            )}

            <Section title="Screening">
              <p className="text-xs text-slate-500 mb-2">Current: {c.screening?.status || "—"}</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid="onb-screen-clear" onClick={() => act(() => api.post("/admin/onboarding/screening/decide", { business_id: businessId, result: "clear" }), "Marked clear")} className="h-8 px-3 rounded bg-emerald-600 text-white text-xs">Clear</button>
                <button data-testid="onb-screen-confirm" onClick={() => act(() => api.post("/admin/onboarding/screening/decide", { business_id: businessId, result: "confirmed_match" }), "Confirmed (compliance blocked)")} className="h-8 px-3 rounded bg-red-600 text-white text-xs">Confirm prohibited</button>
              </div>
            </Section>

            {c.ownership_flags?.length > 0 && (
              <Section title="Ownership flags">
                <div className="flex flex-wrap gap-1">{c.ownership_flags.map((f) => <span key={f} className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700">{f}</span>)}</div>
              </Section>
            )}

            <Section title="Documents">
              <p className="text-xs text-slate-500">{c.document_count} document(s) on file. Government IDs masked: {JSON.stringify(c.government_ids_masked)}</p>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">{title}</h4>
      {children}
    </div>
  );
}
